import { useCallback, useEffect, useRef } from 'react'
import { useHistory, useQueryParams } from '@fs/zion-router'
import { useSlsDispatchContext, useSlsStateContext } from '@fs/zion-sls'
import { useGroupData, useImageOrganizeModes, useImageOrganizeActions } from '../../../views/imageBrowse/hooks'
import useSetFocusedIndex from './useSetFocusedIndex'
import {
  useSharedBrowserDispatchContext,
  useSharedBrowserStateContext,
} from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

const useImageBrowserSetup = () => {
  const { query } = useQueryParams()
  const { view = 'explore' } = query

  const sharedBrowserState = useSharedBrowserStateContext()
  const sharedBrowserDispatch = useSharedBrowserDispatchContext()
  const history = useHistory()

  const { currentImageArk } = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()

  const { itemNumber } = useGroupData()
  const { isDivideMode } = useImageOrganizeModes()
  const { isImageOrganizeMode } = useImageOrganizeModes()
  const { dividerOffsets } = useImageOrganizeActions()

  const setFocusedIndex = useSetFocusedIndex()
  const focusInitialized = useRef(false)

  const thumbnails = sharedBrowserState.thumbnails

  const setIndexFromUrlArk = useCallback(() => {
    const imageArk = history.location.pathname.split('/').pop()
    if (!currentImageArk) zionSlsDispatch({ type: 'SET_DATA', value: { currentImageArk: imageArk } })
    if (thumbnails && thumbnails.length) {
      const idx = thumbnails.findIndex((img) => img.ark === imageArk)
      if (sharedBrowserState.focusedIndex === -1 && idx > -1) {
        sharedBrowserDispatch({ type: 'SET_FOCUSED_INDEX', value: idx })
        focusInitialized.current = true
      }
    }
  }, [
    currentImageArk,
    history.location.pathname,
    sharedBrowserState.focusedIndex,
    zionSlsDispatch,
    thumbnails,
    sharedBrowserDispatch,
  ])

  const setUpIndex = useCallback(() => {
    if (sharedBrowserState.focusedIndex === -1) {
      setIndexFromUrlArk()
    } else {
      const idx = sharedBrowserState.focusedIndex >= thumbnails?.length ? 0 : sharedBrowserState.focusedIndex
      if (idx !== sharedBrowserState.focusedIndex) setFocusedIndex(0)
    }
  }, [sharedBrowserState.focusedIndex, thumbnails, setFocusedIndex, setIndexFromUrlArk])

  const setupExploreView = useCallback(() => {
    if (thumbnails && thumbnails.length) setUpIndex()
  }, [setUpIndex, thumbnails])

  const setUpOrganizeMode = useCallback(() => {
    if (
      (isDivideMode || isImageOrganizeMode) &&
      (typeof sharedBrowserState.focusedIndex !== 'number' || sharedBrowserState.focusedIndex === -1)
    ) {
      if (itemNumber > 0 && dividerOffsets?.length > 0 && isDivideMode && focusInitialized.current) {
        setFocusedIndex(dividerOffsets[itemNumber - 1])
      } else setUpIndex()
    }
  }, [
    dividerOffsets,
    isDivideMode,
    isImageOrganizeMode,
    itemNumber,
    sharedBrowserState.focusedIndex,
    setFocusedIndex,
    setUpIndex,
  ])

  // Initialize the focused index
  // Don’t load the image viewer into memory until the focusedIndex and images have been set.  Otherwise it will load image zero and then load the correct image
  // The first time focusedIndex is set, it's set from the ark in the browser url. We do this by finding it's index in the thumbnails, which were set from the apids
  useEffect(() => {
    if (view === 'organize') setUpOrganizeMode()
  }, [setUpOrganizeMode, view])

  useEffect(() => {
    if (view === 'index' || view === 'fullText') setIndexFromUrlArk()
  }, [view, setIndexFromUrlArk])

  useEffect(() => {
    if (view === 'explore') setupExploreView()
  }, [view, setupExploreView])
}

export default useImageBrowserSetup
